import '../../Styles/BlueprintUploadCard.css';

import type { AxiosProgressEvent } from 'axios';
import type { UploadFile } from 'antd/es/upload/interface';

import axios from 'axios';
import React, { useState } from 'react';
import { Card, Upload, message } from 'antd';

import { takeoffUrl } from '../../globalSettings';
import { getPresignedUrl } from '../../services/projects';

const { Dragger } = Upload;

interface BlueprintUploadProps {
  projectId: string;
  onCancel: () => void;
}

const BlueprintUpload: React.FC<BlueprintUploadProps> = ({ projectId, onCancel }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const handleFileChange = async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
    const latestFile = newFileList[newFileList.length - 1];
    if (latestFile && latestFile.originFileObj) {
      await uploadFileToS3(latestFile.originFileObj);
    }
  };


  const uploadFileToS3 = async (file: File) => {
    setLoading(true);
    setUploadProgress(0);

    try {
      const { url } = await getPresignedUrl(projectId);

      await axios.put(url, file, {
        headers: {
          'Content-Type': 'application/pdf',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        },
      });

      setUploadProgress(100);

      message.success('Blueprint uploaded successfully!');
      window.location.href = `${takeoffUrl}/?project_id=${projectId}`;
    } catch (error) {
      console.error('Error uploading blueprint:', error);
      message.error('Failed to upload blueprint. Please try again.');
      setLoading(false);
      setUploadProgress(null);
    }
  };

  return (
    <div className='container py-4'>
      {uploadProgress == null && (
        <Card className='blueprint-upload-card'>
          <a className='cancel-link' style={{ right: '25px' }} onClick={onCancel}>
            <svg aria-hidden="true" focusable="false" role="presentation" width="24" height="24" viewBox="0 0 53 53" fill="black" xmlns="http://www.w3.org/2000/svg">
              <path d="M41.9587 14.1554L38.8449 11.0416L26.5003 23.3862L14.1557 11.0416L11.042 14.1554L23.3866 26.5L11.042 38.8445L14.1557 41.9583L26.5003 29.6137L38.8449 41.9583L41.9587 38.8445L29.6141 26.5L41.9587 14.1554Z" stroke="none" />
            </svg>
          </a>
          <h3 className='mb-4'>Upload PDF Blueprints</h3>

          <Dragger style={{ display: uploadProgress === null ? 'block' : 'none' }}
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept='.pdf'
            maxCount={1}
            multiple={false}
            disabled={loading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="65" viewBox="0 0 64 65" fill="none">
              <path d="M32 57.5V35M32 35L40.75 43.75M32 35L23.25 43.75" stroke="#F27662" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M52 46.5182C55.7343 45.0552 59.5 41.7222 59.5 35C59.5 25 51.1667 22.5 47 22.5C47 17.5 47 7.5 32 7.5C17 7.5 17 17.5 17 22.5C12.8333 22.5 4.5 25 4.5 35C4.5 41.7222 8.2657 45.0552 12 46.5182" stroke="#F27662" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="ant-upload-text">Click or drag PDF file to this area to upload</p>
            <p className="ant-upload-hint">Support PDF. Maximum file size 20MB.</p>
          </Dragger>
        </Card>
      )}


      {uploadProgress !== null && (
        <div className="upload-progress-container">
          <a className='cancel-link' style={{ right: '80px' }} onClick={onCancel}>
            <svg aria-hidden="true" focusable="false" role="presentation" width="24" height="24" viewBox="0 0 53 53" fill="black" xmlns="http://www.w3.org/2000/svg">
              <path d="M41.9587 14.1554L38.8449 11.0416L26.5003 23.3862L14.1557 11.0416L11.042 14.1554L23.3866 26.5L11.042 38.8445L14.1557 41.9583L26.5003 29.6137L38.8449 41.9583L41.9587 38.8445L29.6141 26.5L41.9587 14.1554Z" stroke="none" />
            </svg>
          </a>
          <h3 className='mb-4'>Upload PDF Blueprints</h3>
          <div className='upload-progress-inner-container'>
            <svg xmlns="http://www.w3.org/2000/svg" width="66" height="64" viewBox="0 0 66 64" fill="none">
              <path d="M65.2373 49.4756L50.7129 64H0.765625V16.1465C0.769995 11.8653 2.47266 7.76048 5.5 4.7334C8.52754 1.70631 12.6328 0.0037729 16.9141 0H65.2373V49.4756ZM16.9141 4.28223C13.769 4.28662 10.7539 5.53761 8.53027 7.76172C6.30662 9.98585 5.0555 13.0014 5.05176 16.1465V59.7139H48.9424L60.9531 47.6885V4.28223H16.9141ZM22.002 49.3623H15.6357V42.9971H22.002V49.3623ZM36.1855 49.3623H29.8193V42.9971H36.1855V49.3623ZM44 49.3623V42.9971H50.3662L44 49.3623ZM22.002 35.1855H15.6357V28.8193H22.002V35.1855ZM36.1855 35.1855H29.8193V28.8193H36.1855V35.1855ZM50.3662 35.1855H44V28.8193H50.3662V35.1855ZM22.002 21.0029H15.6357C15.6358 19.3148 16.3064 17.6957 17.5 16.502C18.6939 15.3081 20.3136 14.6377 22.002 14.6377V21.0029ZM36.1855 21.0029H29.8193V14.6377H36.1855V21.0029ZM50.3662 21.0029H44V14.6377H50.3662V21.0029Z" fill="#F27662" />
            </svg>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="upload-progress-text">
              <p>Uploading... </p>
              <span>{fileList[0]?.name}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintUpload;
